import React from 'react';

export type AppLoaderSize = 'small' | 'medium' | 'large';

interface AppLoaderProps {
  /** Diameter of the spinner. Defaults to `medium`. */
  size?: AppLoaderSize;
  /** Text shown under the spinner. Also what a screen reader announces. */
  message?: string;
  /**
   * Accessible name used when no visible `message` is given.
   * Defaults to `'Loading'`.
   */
  label?: string;
  /** Extra classes on the wrapper, so callers own the surrounding spacing. */
  className?: string;
}

const RING_SIZES: Record<AppLoaderSize, string> = {
  small: 'w-12 h-12',
  medium: 'w-20 h-20',
  large: 'w-32 h-32',
};

const BORDER_WIDTHS: Record<AppLoaderSize, string> = {
  small: 'border-2',
  medium: 'border-2',
  large: 'border-4',
};

const LOGO_SIZES: Record<AppLoaderSize, string> = {
  small: 'text-2xl',
  medium: 'text-4xl',
  large: 'text-6xl',
};

/**
 * Branded loading indicator.
 *
 * The wrapper is a live region so assistive technology announces that something is
 * loading; the rings and logo are decorative and hidden from it. Only the outer ring
 * animates when the user has asked for reduced motion — that single rotation is the
 * essential progress signal, while the counter-rotation, glow and scale pulse stack
 * into enough simultaneous movement to be uncomfortable.
 *
 * The component deliberately applies no outer padding. Callers position it, which
 * keeps it usable inline (a modal result list) and full-page (`LoadingSpinner`).
 */
export const AppLoader: React.FC<AppLoaderProps> = ({
  size = 'medium',
  message,
  label = 'Loading',
  className = '',
}) => {
  const border = BORDER_WIDTHS[size];

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex flex-col items-center justify-center ${className}`.trim()}
    >
      <div className={`relative ${RING_SIZES[size]} mx-auto`} aria-hidden="true">
        {/* Outer rotating ring */}
        <div
          className={`absolute inset-0 rounded-full ${border} border-transparent border-t-indigo-500 border-r-purple-500 animate-spin`}
        />

        {/* Counter-rotating ring */}
        <div
          className={`absolute inset-1 rounded-full ${border} border-transparent border-b-indigo-400 border-l-purple-400 motion-safe:animate-spin-slow`}
        />

        {/* Inner glow */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 blur-lg motion-safe:animate-pulse" />

        {/* Center logo */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className={`${LOGO_SIZES[size]} font-bold bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 bg-clip-text text-transparent motion-safe:animate-code-pulse`}
          >
            &lt;/&gt;
          </div>
        </div>
      </div>

      {/* A live region announces its contents, not its label, so there is always
          text here — visually hidden when the caller wants no visible message. */}
      {message ? (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 motion-safe:animate-fade-in">
          {message}
        </p>
      ) : (
        <span className="sr-only">{label}</span>
      )}
    </div>
  );
};
